<template>
  <div class="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
    <div class="w-full max-w-md">
      <div id="login-card" class="w-full">
          <!-- Logo -->
          <div class="flex items-center justify-center gap-4 mb-6 w-full">
            <!-- Favicon icon -->
            <img 
              src="/favicon.png" 
              alt="Kaizen" 
              class="object-contain flex-shrink-0"
              style="width: 70px; height: 70px;"
            />
            <!-- Kaizen text -->
            <h2 class="text-5xl font-semibold text-gray-900 dark:text-white leading-none">Kaizen</h2>
          </div>

          <!-- Authentik Login Button -->
          <div class="mb-4 w-full">
            <HLButton
              id="authentik-login-btn"
              type="button"
              color="blue"
              variant="primary"
              size="lg"
              :disabled="loading"
              :loading="loading"
              block
              @click="handleAuthentikLogin"
            >
              {{ loading ? 'Redirecting...' : 'Sign in with Authentik' }}
            </HLButton>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-4 text-center">
              Use your Authentik account to continue
            </p>
          </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'
import { http, storeTokens } from '@/utils/httpClient'
import { 
  HLButton
} from '@platform-ui/highrise'

// Router and stores
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toast = useToast()

// Reactive data
const loading = ref(false)

// Authentik Login Handler
const handleAuthentikLogin = async () => {
  try {
    loading.value = true
    
    // Generate state for CSRF protection
    const state = generateRandomString(32)
    sessionStorage.setItem('authentik_state', state)
    
    // Get authorization URL from backend
    const response = await http.get(`/auth/authentik/authorization-url?state=${encodeURIComponent(state)}`)
    
    if (response.authorizationUrl) {
      // Redirect to Authentik authorization page
      window.location.href = response.authorizationUrl
    } else {
      throw new Error('Failed to get authorization URL')
    }
  } catch (error) {
    console.error('Authentik login error:', error)
    toast.error('Authentik login failed. Please try again.')
    loading.value = false
  }
}

// Handle Authentik callback
const handleAuthentikCallback = async (code, state) => {
  try {
    loading.value = true
    
    // Verify state to prevent CSRF attacks
    const storedState = sessionStorage.getItem('authentik_state')
    if (storedState !== state) {
      throw new Error('Invalid state parameter. Possible CSRF attack.')
    }
    sessionStorage.removeItem('authentik_state')
    
    // Exchange code for tokens
    const result = await http.post('/auth/authentik/token', {
      code,
      state
    })
    
    // Store tokens
    storeTokens(result.accessToken, result.refreshToken, true)
    
    // Update auth store
    authStore.user = {
      id: result.user.id,
      uid: result.user.id,
      email: result.user.email,
      name: result.user.name,
      displayName: result.user.name,
      role: result.user.role
    }
    authStore.userProfile = result.user
    
    // Store session
    localStorage.setItem('user_session', JSON.stringify(authStore.user))
    localStorage.removeItem('user_logged_out')
    
    toast.success('Successfully signed in with Authentik!')
    
    // Clean up URL parameters
    router.replace('/login')
    
    // Redirect to dashboard
    router.push('/dashboard')
  } catch (error) {
    console.error('Authentik callback error:', error)
    
    let errorMessage = 'Authentik authentication failed. Please try again.'
    
    if (error.message) {
      errorMessage = error.message
    } else if (error.response?.data?.error?.message) {
      errorMessage = error.response.data.error.message
    }
    
    toast.error(errorMessage)
    
    // Clean up URL parameters
    router.replace('/login')
  } finally {
    loading.value = false
  }
}

// Generate random string for state parameter
const generateRandomString = (length) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

// Lifecycle
onMounted(() => {
  // If already authenticated, redirect to dashboard
  if (authStore.isAuthenticated) {
    router.push('/dashboard')
    return
  }
  
  // Check for Authentik callback (code and state in URL)
  const code = route.query.code
  const state = route.query.state
  
  if (code && state) {
    handleAuthentikCallback(code, state)
  }
})
</script>

